/**
 * Parse shock vs wheel-travel CSV and fit cubic y = a + b*x + c*x^2 + d*x^3.
 * Returns { ok, points, coefficients: { rearA, rearB, rearC, rearD }, rearTravel, error? }
 */
export function fitLinkagePolynomialFromCsvText(text) {
  const lines = text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  const points = [];
  for (let i = 0; i < lines.length; i += 1) {
    const normalized = lines[i].replace(/;/g, ',');
    const parts = normalized.split(',').map((p) => p.trim());
    if (parts.length < 2) continue;
    const x = Number(parts[0]);
    const y = Number(parts[1]);
    if (Number.isFinite(x) && Number.isFinite(y)) points.push({ x, y });
  }
  if (points.length < 4) {
    return { ok: false, error: 'At least 4 numeric rows are required.', points };
  }
  const n = points.length;
  let sumX = 0;
  let sumX2 = 0;
  let sumX3 = 0;
  let sumX4 = 0;
  let sumX5 = 0;
  let sumX6 = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumX2Y = 0;
  let sumX3Y = 0;
  points.forEach(({ x, y }) => {
    const x2 = x * x;
    const x3 = x2 * x;
    const x4 = x3 * x;
    const x5 = x4 * x;
    const x6 = x5 * x;
    sumX += x;
    sumX2 += x2;
    sumX3 += x3;
    sumX4 += x4;
    sumX5 += x5;
    sumX6 += x6;
    sumY += y;
    sumXY += x * y;
    sumX2Y += x2 * y;
    sumX3Y += x3 * y;
  });
  const A = [
    [n, sumX, sumX2, sumX3],
    [sumX, sumX2, sumX3, sumX4],
    [sumX2, sumX3, sumX4, sumX5],
    [sumX3, sumX4, sumX5, sumX6],
  ];
  const b = [sumY, sumXY, sumX2Y, sumX3Y];
  for (let i = 0; i < 4; i += 1) {
    let pivot = i;
    for (let j = i + 1; j < 4; j += 1) {
      if (Math.abs(A[j][i]) > Math.abs(A[pivot][i])) pivot = j;
    }
    if (pivot !== i) {
      [A[i], A[pivot]] = [A[pivot], A[i]];
      [b[i], b[pivot]] = [b[pivot], b[i]];
    }
    const diag = A[i][i];
    if (!Number.isFinite(diag) || Math.abs(diag) < 1e-12) {
      return { ok: false, error: 'Could not solve linkage polynomial (singular matrix).', points };
    }
    for (let k = i; k < 4; k += 1) A[i][k] /= diag;
    b[i] /= diag;
    for (let j = 0; j < 4; j += 1) {
      if (j === i) continue;
      const factor = A[j][i];
      for (let k = i; k < 4; k += 1) A[j][k] -= factor * A[i][k];
      b[j] -= factor * b[i];
    }
  }
  const [a, b1, c, d] = b;
  const maxWheel = Math.max(...points.map((p) => p.y));
  return {
    ok: true,
    points,
    coefficients: {
      rearA: Number(a.toFixed(6)),
      rearB: Number(b1.toFixed(6)),
      rearC: Number(c.toFixed(8)),
      rearD: Number(d.toFixed(10)),
    },
    rearTravel: Math.round(maxWheel),
  };
}
